import Cookie from 'js-cookie';
import { useCallback, useMemo, useState } from 'react';
import { getTokenWithPromise } from '@cld/session-service';

import type { UnicornWrapperProps } from './UnicornWrapper';
import { UnicornWrapper } from './UnicornWrapper';
import { createAnalytics } from './api/createAnalytics';
import { getEnvironmentData } from './state/domain/getEnvironmentData';
import { createConsoleLogger } from './api/createConsoleLogger';
import { createStorageClient } from './api/createStorageClient';
import { decorateStorageWithLogger } from './api/decorateStorageWithLogger';
import { createAbTestingClientFactory, createAlwaysFailingAbTestingClientFactory } from './api/createAbTestingClient';
import { createExternalLoggerFactory } from './api/createExternalLoggerFactory';
import type { GetAuthenticationToken, LocationApi } from './state/types';

type UnicornWrapperInjectedProps = Pick<UnicornWrapperProps, 'children'>;

const STORAGE_PREFIX = '@cld/unicorn/';
const ROLLBAR_UNICORN_TOKEN = 'dfc48fcc25ee4ada8c242c728c5fde92';

// Only create dependencies which do not rely on loading additional data
// Everthing that is glued together here must have data ready immediately
// So data like account, product environment or user should not be available here
export const UnicornWrapperInjected = ({ children }: UnicornWrapperInjectedProps) => {
  const environmentData = useMemo(() => getEnvironmentData(window.location.href), []);
  const devMode = useMemo(() => localStorage.getItem('@cld/unicorn/dev-mode') === 'true', []);

  const persistentStorage = useMemo(
    () =>
      createStorageClient({
        keyPrefix: STORAGE_PREFIX,
        storageApi: window.localStorage,
      }),
    []
  );
  const consoleLogger = useMemo(
    () =>
      createConsoleLogger({
        devMode,
        storage: persistentStorage,
      }),
    [devMode, persistentStorage]
  );
  const persistentStorageWithLogging = useMemo(() => decorateStorageWithLogger(persistentStorage, consoleLogger), [persistentStorage, consoleLogger]);
  // FIXME: should not have state, maybe we can reorganize this?
  const externalLoggerFactory = useMemo(
    () =>
      createExternalLoggerFactory({
        environmentName: environmentData.name,
        consoleLogger,
      }),
    [environmentData.name, consoleLogger]
  );
  const unicornExternalLogger = useMemo(() => externalLoggerFactory.createInstance(ROLLBAR_UNICORN_TOKEN), [externalLoggerFactory]);
  const systemTheme = useMemo(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  }, []);

  const splitErrorStorage = useMemo(() => persistentStorage.createController<boolean>('force-split-error'), [persistentStorage]);
  const forceSplitErrorResult = useMemo(() => splitErrorStorage.get(), [splitErrorStorage]);
  const [forceSplitError, setForceSplitError] = useState(forceSplitErrorResult.status === 'success' ? forceSplitErrorResult.value : false);
  const setForceSplitErrorWithPersistence = useCallback(
    (newValue: boolean) => {
      setForceSplitError(newValue);
      splitErrorStorage.set(newValue);
    },
    [setForceSplitError, splitErrorStorage]
  );
  const abTestingClientFactory = useMemo(
    () =>
      forceSplitError
        ? createAlwaysFailingAbTestingClientFactory({ consoleLogger })
        : createAbTestingClientFactory({
            baseFetch: window.fetch,
            environmentName: environmentData.name,
            consoleLogger,
            persistentStorage: persistentStorageWithLogging,
          }),
    [forceSplitError, consoleLogger, environmentData.name, persistentStorageWithLogging]
  );

  const enableTheming = useMemo(() => {
    const enableThemingStorage = persistentStorage.createController<boolean>('enable-theming');
    const enableThemingStorageResult = enableThemingStorage.get();
    return enableThemingStorageResult.status === 'success' ? enableThemingStorageResult.value : false;
  }, [persistentStorage]);

  const analytics = useMemo(() => createAnalytics({ consoleLogger }), [consoleLogger]);

  const locationApi: LocationApi = useMemo(
    () => ({
      getHref: () => window.location.href,
      redirect: (newHref: string, options) => {
        if (options?.replace) {
          return window.location.replace(newHref);
        }

        return (window.location.href = newHref);
      },
    }),
    []
  );

  const getAuthenticationToken: GetAuthenticationToken = useMemo(
    () => async () => {
      try {
        const token = await getTokenWithPromise();

        if (!token || token?.length === 0) {
          return { kind: 'error' };
        }

        return { kind: 'success', token };
      } catch (_) {
        return { kind: 'error' };
      }
    },
    []
  );

  return (
    <unicornExternalLogger.ErrorBoundary errorBoundaryLogLabel="Unicorn Wrapper">
      <UnicornWrapper
        analytics={analytics}
        abTestingClientFactory={abTestingClientFactory}
        devMode={devMode}
        environmentData={environmentData}
        forceSplitError={forceSplitError}
        getAuthenticationToken={getAuthenticationToken}
        consoleLogger={consoleLogger}
        unicornExternalLogger={unicornExternalLogger}
        externalLoggerFactory={externalLoggerFactory}
        setForceSplitError={setForceSplitErrorWithPersistence}
        cookieApi={Cookie}
        localStorageClient={window.localStorage}
        enableTheming={enableTheming}
        systemTheme={systemTheme}
        locationApi={locationApi}
        unicornStorageClient={persistentStorageWithLogging}
      >
        {children}
      </UnicornWrapper>
    </unicornExternalLogger.ErrorBoundary>
  );
};
